import React, { useEffect, useMemo, useState } from "react";
import { Row, Col, Popover } from "antd";
import { BsArrowRight } from "react-icons/bs";
import WidgetCard from "./WidgetCard";
import RatingsWidget from "./RatingsWidget";
import { ISummaryWidgetProps, ISummaryWidgetConfig, ProductDeepdiveModules, SummaryWidgetConfigKeys, OptionValues, CommonWidgetMetrics, DeepDiveModuleTypes } from "@ts/common-types";
import { includes } from "underscore";
import { titleCase } from "@ts/utils";
import { SUMMARY_WIDGET_UNITS } from "@ts/constants";
import OtherSellersDataModalWidget from "./OtherSellersDataModalWidget";

const NoDataComponent = () => (
  <div className="d-flex fs-6 align-items-center justify-content-center text-secondary mb-3">
    No Data
  </div>
);

const SummaryWidget: React.FC<ISummaryWidgetProps> = ({
  widgetData,
  onClickMoreHandler,
  activeTab,
  moduleType = "",
}) => {
  const [showNoDataCondition, setShowNoDataCondition] = useState(
    widgetData === null || widgetData === undefined
  );

  const widgetConfig: ISummaryWidgetConfig[] = useMemo(() => {
    const configMapping: Record<string, ISummaryWidgetConfig[]> = {
        [ProductDeepdiveModules.PRICING]: [
            {
                key: SummaryWidgetConfigKeys.PRICE,
                title: "Average Price",
                valueKey: SummaryWidgetConfigKeys.PRICE_VALUE,
                changeKey: SummaryWidgetConfigKeys.PRICE_CHANGE,
                additionalInfo: (data) => (
                    <>
                        <div className="small mb-2">
                            <span>{data?.price?.unitPrice} / Unit</span>
                        </div>
                        <div className="small">
                            <span
                                className={
                                    data?.price?.change > 0
                                        ? "text-success"
                                        : "text-danger"
                                }
                            >
                                {data?.price?.change >= 0
                                    ? CommonWidgetMetrics.INCREASE
                                    : data?.price?.change < 0
                                    ? CommonWidgetMetrics.DECREASE
                                    : CommonWidgetMetrics.CONSISTENT}
                            </span>{" "}
                            as compared to prev period
                        </div>
                    </>
                ),
            },
            {
                key: SummaryWidgetConfigKeys.RPI,
                title: SummaryWidgetConfigKeys.RPI.toUpperCase(),
                valueKey: SummaryWidgetConfigKeys.RPI_VALUE,
                changeKey: SummaryWidgetConfigKeys.RPI_CHANGE,
                additionalInfo: (data) => (
                    <div className="small">
                        {data?.rpi?.percentage}%{" "}
                        <span className="text-danger">
                            {data?.rpi?.status === CommonWidgetMetrics.ABOVE
                                ? CommonWidgetMetrics.ABOVE
                                : CommonWidgetMetrics.BELOW}
                        </span>{" "}
                        Avg. Competition Price
                    </div>
                ),
            },
            {
                key: SummaryWidgetConfigKeys.AVG_DEAL_PERC,
                title: "Average Deal %",
                valueKey: SummaryWidgetConfigKeys.AVG_DEAL_VALUE,
                changeKey: SummaryWidgetConfigKeys.AVG_DEAL_CHANGE,
                additionalInfo: (data) => (
                    <div className="small">
                        Mostly{" "}
                        <span
                            className={
                                data?.averageDealPercentage?.change >= 0
                                    ? "text-success"
                                    : "text-danger"
                            }
                        >
                            {data?.averageDealPercentage?.change > 0
                                ? CommonWidgetMetrics.INCREASE
                                : data?.averageDealPercentage?.change < 0
                                ? CommonWidgetMetrics.DECREASE
                                : CommonWidgetMetrics.CONSISTENT}
                        </span>{" "}
                        compared to prev period
                    </div>
                ),
            },
        ],
        [ProductDeepdiveModules.RATINGS_REVIEWS]: [
            {
                key: SummaryWidgetConfigKeys.RATING,
                title: titleCase(SummaryWidgetConfigKeys.RATING),
                valueKey: SummaryWidgetConfigKeys.RATING_VALUE,
                changeKey: SummaryWidgetConfigKeys.RATING_KEY,
                additionalInfo: (data) => (
                    <div className="small">
                        Consistently{" "}
                        <span
                            className={
                                data?.rating?.change >= 0
                                    ? "text-success"
                                    : "text-danger"
                            }
                        >
                            {data?.rating?.change > 0
                                ? CommonWidgetMetrics.INCREASE
                                : data?.rating?.change < 0
                                ? CommonWidgetMetrics.DECREASE
                                : CommonWidgetMetrics.CONSISTENT}
                        </span>{" "}
                        in ratings
                    </div>
                ),
            },
            {
                key: SummaryWidgetConfigKeys.NUM_RATINGS,
                title: "#Ratings",
                valueKey: SummaryWidgetConfigKeys.NUM_RATINGS_VALUE,
                changeKey: SummaryWidgetConfigKeys.NUM_RATINGS_CHANGE,
                additionalInfo: (data) => (
                    <div className="small">
                        Rating Velocity{" "}
                        <span
                            className={
                                data?.numRatings?.velocity >= 0
                                    ? "text-success"
                                    : "text-danger"
                            }
                        >
                            {data?.numRatings?.velocity > 0
                                ? CommonWidgetMetrics.INCREASE
                                : data?.numRatings?.velocity < 0
                                ? CommonWidgetMetrics.DECREASE
                                : CommonWidgetMetrics.CONSISTENT}
                        </span>{" "}
                        compared to prev period
                    </div>
                ),
            },
            {
                key: SummaryWidgetConfigKeys.REVIEWS,
                title: "#Reviews",
                valueKey: SummaryWidgetConfigKeys.REVIEWS_VALUE,
                changeKey: SummaryWidgetConfigKeys.REVIEWS_CHANGE,
                additionalInfo: (data) => (
                    <div className="small">
                        <span className="text-warning">
                            {data?.reviews?.averageRating}
                        </span>{" "}
                        is the average rating of recent 10 reviews
                    </div>
                ),
                popoverContent: (data) => (
                    <RatingsWidget type="widget" widgetData={data?.ratings} />
                ),
                onClickMore: () => onClickMoreHandler?.(OptionValues.ALL),
            },
        ],
        [ProductDeepdiveModules.VISIBILITY]: [
            {
                key: SummaryWidgetConfigKeys.ORGANIC_VISIBILITY,
                title: "Organic Visibility",
                valueKey: SummaryWidgetConfigKeys.ORGANIC_VISIBILITY_VALUE,
                changeKey: SummaryWidgetConfigKeys.ORGANIC_VISIBILITY_CHANGE,
                additionalInfo: (data) => (
                    <>
                        <div className="small">
                            <span
                                className={
                                    data?.organicVisibility?.change >= 0
                                        ? "text-success"
                                        : "text-danger"
                                }
                            >
                                {data?.organicVisibility?.change > 0
                                    ? CommonWidgetMetrics.INCREASE
                                    : data?.organicVisibility?.change < 0
                                    ? CommonWidgetMetrics.DECREASE
                                    : CommonWidgetMetrics.CONSISTENT}
                            </span>{" "}
                            as compared to prev period
                        </div>
                    </>
                ),
            },
            {
                key: SummaryWidgetConfigKeys.PAID_VISIBILITY,
                title: "Paid Visibility",
                valueKey: SummaryWidgetConfigKeys.PAID_VISIBILITY_VALUE,
                changeKey: SummaryWidgetConfigKeys.PAID_VISIBILITY_CHANGE,
                additionalInfo: (data) => (
                    <div className="small">
                        {data?.paidVisibility?.percentage?.toFixed(2)}%{" "}
                        <span
                            className={
                                data?.paidVisibility?.status ===
                                CommonWidgetMetrics.ABOVE
                                    ? "text-success"
                                    : "text-danger"
                            }
                        >
                            {data?.paidVisibility?.status ===
                            CommonWidgetMetrics.ABOVE
                                ? CommonWidgetMetrics.ABOVE
                                : CommonWidgetMetrics.BELOW}
                        </span>{" "}
                        Avg. Competition Price
                    </div>
                ),
            },
            {
                key: SummaryWidgetConfigKeys.GV,
                title: "GV",
                valueKey: SummaryWidgetConfigKeys.GV_VALUE,
                changeKey: SummaryWidgetConfigKeys.GV_CHANGE,
                additionalInfo: (data) => (
                    <div className="small">
                        Mostly{" "}
                        <span
                            className={
                                data?.GV?.change >= 0
                                    ? "text-success"
                                    : "text-danger"
                            }
                        >
                            {data?.GV?.change > 0
                                ? CommonWidgetMetrics.INCREASE
                                : data?.GV?.change < 0
                                ? CommonWidgetMetrics.DECREASE
                                : CommonWidgetMetrics.CONSISTENT}
                        </span>{" "}
                        compared to prev period
                    </div>
                ),
            },
        ],
        [ProductDeepdiveModules.AVAILABILITY]: [
            {
                key: SummaryWidgetConfigKeys.AVAILABILITY_PERCENTAGE,
                title: "Availability%",
                valueKey: SummaryWidgetConfigKeys.AVAILABILITY_PERCENTAGE_VALUE,
                changeKey:
                    SummaryWidgetConfigKeys.AVAILABILITY_PERCENTAGE_CHANGE,
                additionalInfo: (data) => (
                    <div className="small">
                        Out of stock since{" "}
                        <span
                            className={
                                data?.availabilityPercentage?.outOfstockSince >=
                                0
                                    ? "text-success"
                                    : "text-danger"
                            }
                        >
                            {data?.availabilityPercentage?.outOfstockSince}
                            {" days"}
                        </span>
                    </div>
                ),
            },
            {
                key: SummaryWidgetConfigKeys.BUYBOX_PERCENTAGE,
                title: "Buybox%",
                valueKey: SummaryWidgetConfigKeys.BUYBOX_PERCENTAGE_VALUE,
                changeKey: SummaryWidgetConfigKeys.BUYBOX_PERCENTAGE_CHANGE,
                additionalInfo: (data) => (
                    <div className="small">
                        Buybox Winner:{" "}
                        <span
                            className={
                                data?.buyboxPercentage?.winner
                                    ? "text-success"
                                    : "text-danger"
                            }
                        >
                            {data?.buyboxPercentage?.winner}
                        </span>
                    </div>
                ),
                onClickMore: () => onClickMoreHandler?.(OptionValues.ALL),
                onClickMoreTitle: "Other Sellers Data",
            },
            {
                key: SummaryWidgetConfigKeys.ESTIMATED_SALES_LOSS,
                title: "Estimated Sales Loss",
                valueKey: SummaryWidgetConfigKeys.ESTIMATED_SALES_LOSS_VALUE,
                changeKey: SummaryWidgetConfigKeys.ESTIMATED_SALES_LOSS_CHANGE,
                additionalInfo: (data) => (
                    <div className="small">
                        <span
                            className={
                                data?.estimatedSalesLoss?.change <= 0
                                    ? "text-success"
                                    : "text-danger"
                            }
                        >
                            {data?.estimatedSalesLoss?.change >= 0
                                ? CommonWidgetMetrics.GREATER_THAN
                                : CommonWidgetMetrics.LESS_THAN}
                        </span>{" "}
                        previous period
                    </div>
                ),
            },
        ],
    };
    return configMapping[activeTab];
      
  },[activeTab]);

  const widgetConfigCategoryInsightsQcomm: ISummaryWidgetConfig[] = useMemo(() => {
    const configMapping: Record<string, ISummaryWidgetConfig[]> = {
        [ProductDeepdiveModules.AVAILABILITY]: [
            {
                key: SummaryWidgetConfigKeys.WT_AVAILIBILITY,
                title: "Wt. Availabilitiy%",
                valueKey: SummaryWidgetConfigKeys.WT_AVAILIBILITY_VALUE,
                changeKey: SummaryWidgetConfigKeys.WT_AVAILIBILITY_CHANGE,
                additionalInfo: (data) => (
                    <div className="small">
                        <span
                            className={
                                data?.wtAvailability?.severeSkus<1
                                    ? "text-success"
                                    : "text-danger"
                            }
                        >
                            {data?.wtAvailability?.severeSkus} SKUs 
                        </span>{" "}
                        with Severe Availabilitiy Issue
                    </div>
                ),
                onClickMore: () => onClickMoreHandler?.(OptionValues.OOS_SKUS),
                onClickMoreTitle: "OOS Skus",
            },
            {
                key: SummaryWidgetConfigKeys.ASSORTMENT,
                title: "Assortment",
                valueKey: SummaryWidgetConfigKeys.ASSORTMENT_VALUE,
                changeKey:
                    SummaryWidgetConfigKeys.ASSORTMENT_CHANGE,
                additionalInfo: (data) => (
                    <div className="small">
                        <span
                            className={
                                data?.assortment?.notAvailableSkus <1
                                    ? "text-success"
                                    : "text-danger"
                            }
                        >
                            {data?.assortment?.notAvailableSkus} SKUs
                        </span>
                        {"  with Availability Issue"}
                    </div>
                ),
            },
            {
                key: SummaryWidgetConfigKeys.LISTING_STATUS,
                title: "Listing Status",
                valueKey: SummaryWidgetConfigKeys.LISTING_STATUS_VALUE,
                changeKey: SummaryWidgetConfigKeys.LISTING_STATUS_CHANGE,
                additionalInfo: (data) => (
                    <div className="small">
                        <span
                            className={
                                data?.listingStatus?.lessAvailableSkus <1
                                    ? "text-success"
                                    : "text-danger"
                            }
                        >
                            {data?.listingStatus?.lessAvailableSkus } Sub Cat
                        </span>
                        {" < 50% availability"}
                    </div>
                ),
            },
            {
                key: SummaryWidgetConfigKeys.ESTIMATED_SALES_LOSS,
                title: "Estimated Sales Loss",
                valueKey: SummaryWidgetConfigKeys.ESTIMATED_SALES_LOSS_VALUE,
                changeKey: SummaryWidgetConfigKeys.ESTIMATED_SALES_LOSS_CHANGE,
                additionalInfo: (data) => (
                    <div className="small">
                        <span
                            className={
                                data?.estimatedSalesLoss?.status !== "Greater than"
                                    ? "text-success"
                                    : "text-danger"
                            }
                        >
                            {data?.estimatedSalesLoss?.status}
                        </span>
                        {" previous period"}
                    </div>
                ),
                onClickMore: () => onClickMoreHandler?.(OptionValues.DRAINERS),
                onClickMoreTitle: "Drainers",
            },
        ],
    };
    return configMapping[activeTab];
      
  },[activeTab]);
  
  const widgetConfigCategoryInsightsEcomm: ISummaryWidgetConfig[] = useMemo(() => {
    const configMapping: Record<string, ISummaryWidgetConfig[]> = {
        [ProductDeepdiveModules.AVAILABILITY]: [
            {
                key: SummaryWidgetConfigKeys.AVAILABILITY_PERCENTAGE,
                title: "Availability%",
                valueKey: SummaryWidgetConfigKeys.AVAILABILITY_PERCENTAGE_VALUE,
                changeKey:
                    SummaryWidgetConfigKeys.AVAILABILITY_PERCENTAGE_CHANGE,
                additionalInfo: (data) => (
                    <div className="small">
                        {/* <span
                            className={
                                data?.availabilityPercentage?.outOfstockSince >=
                                0
                                    ? "text-success"
                                    : "text-danger"
                            }
                        >
                            {data?.availabilityPercentage?.outOfstockSince}
                        </span>with Availability Issue */}
                    </div>
                ),
            },
            {
                key: SummaryWidgetConfigKeys.BUYBOX_PERCENTAGE,
                title: "Buybox%",
                valueKey: SummaryWidgetConfigKeys.BUYBOX_PERCENTAGE_VALUE,
                changeKey: SummaryWidgetConfigKeys.BUYBOX_PERCENTAGE_CHANGE,
                additionalInfo: (data) => (
                    <div className="small">
                        {/* <span
                            className={
                                data?.buyboxPercentage?.winner
                                    ? "text-success"
                                    : "text-danger"
                            }
                        >
                            {data?.buyboxPercentage?.winner}
                        </span>
                        with Buybox Issue */}
                    </div>
                ),
                onClickMore: () => onClickMoreHandler?.(OptionValues.SELLERS_ECOMM),
                onClickMoreTitle: "Sellers",
            },
            {
                key: SummaryWidgetConfigKeys.ESTIMATED_SALES_LOSS,
                title: "Estimated Sales Loss",
                valueKey: SummaryWidgetConfigKeys.ESTIMATED_SALES_LOSS_VALUE,
                changeKey: SummaryWidgetConfigKeys.ESTIMATED_SALES_LOSS_CHANGE,
                additionalInfo: (data) => (
                    <div className="small">
                        {/* <span
                            className={
                                data?.estimatedSalesLoss?.change <= 0
                                    ? "text-success"
                                    : "text-danger"
                            }
                        >
                            {data?.estimatedSalesLoss?.change >= 0
                                ? CommonWidgetMetrics.GREATER_THAN
                                : CommonWidgetMetrics.LESS_THAN}
                        </span>{" "}
                        previous period */}
                    </div>
                ),
                onClickMore: () => onClickMoreHandler?.(OptionValues.DRAINERS),
                onClickMoreTitle: "Top Sub Categories",
            },
        ],
    };
    return configMapping[activeTab];
      
  },[activeTab]);

    const widgetConfigCategoryInsights = useMemo(() => {
        if (moduleType === DeepDiveModuleTypes.CATEGORY_QCOMM) {
        return widgetConfigCategoryInsightsQcomm;
        } else if (moduleType === DeepDiveModuleTypes.CATEGORY_ECOMM) {
        return widgetConfigCategoryInsightsEcomm;
        }
        return [];
    }, [moduleType]);

  useEffect(() => {
    if (widgetData === null || widgetData === undefined) {
      setShowNoDataCondition(true);
    } else {
      setShowNoDataCondition(false);
    }
  },[widgetData]);

  const getValue = (path: string, data: any) =>
    path.split(".").reduce((acc, key) => acc?.[key], data);

  return (
    <Row gutter={[16, 16]} className="my-3 py-3 mb-0 pb-0">
      {(moduleType ? widgetConfigCategoryInsights : widgetConfig)?.map((config) => (
        <Col key={config.key} span={moduleType === "category" ? 6 :8}>
          <WidgetCard className="h-100">
            <p className="text-muted d-flex justify-content-between">
              {config.title}
              {config.onClickMore && (
                <span
                  role="button"
                  onClick={config.onClickMore}
                  className="link-primary link-offset-2 link-underline-opacity-25 link-underline-opacity-100-hover small"
                >
                  {config.onClickMoreTitle ? config.onClickMoreTitle : "More"} <BsArrowRight />
                </span>
              )}
            </p>
            <Popover
              placement="left"
              overlayClassName="rounded-2"
              content={
                config.popoverContent &&
                config.popoverContent(widgetData)
              }
            >
              {showNoDataCondition ? (
                <NoDataComponent />
              ) : (
                <>
                <h3 className="mb-3">
                {getValue(config.valueKey, widgetData)}{" "}
                {includes(SUMMARY_WIDGET_UNITS.value,config.valueKey) ? "% " : ""}

                <small
                  className={`fs-5 ${
                    getValue(config.changeKey, widgetData) >= 0
                      ? "text-success"
                      : "text-danger"
                  }`}
                >
                  {getValue(config.changeKey, widgetData) > 0 ? " +" : ""}
                  {getValue(config.changeKey, widgetData)}
                  {includes(SUMMARY_WIDGET_UNITS.change,config.changeKey) ? " %" : ""}
                </small>
              </h3>
                {config.additionalInfo && config.additionalInfo(widgetData)}
                </>
              )}
            </Popover>
          </WidgetCard>
        </Col>
      ))}
    </Row>
  );
};

export default SummaryWidget;